import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import math from 'mathjs';
import { ErrorDialog } from './dialog/error/error';
import { AlertDialog } from './dialog/alert/alert';
import { ConfirmDialog } from './dialog/confirm/confirm';
import { GCodeDialog } from './dialog/gCode/gCode';
import { PdfInfoDialog } from './dialog/pdfInfo/pdfInfo';
import { WorkOfflineDialog } from './dialog/workOffline/workOffline';
import { Message } from './dialog/message.interface';
import { CacheService } from 'ionic-cache';
import { ImageConstants } from './image-constants';
declare var Apptentive;

@Injectable()
export class Utils {
  public paramIcon: HTMLImageElement = new Image();
  constructor(
    private platform: Platform,
    public dialog: MatDialog,
    private cache: CacheService,
    public imageConstants: ImageConstants
  ) {
    this.paramIcon.src = imageConstants.iosShare;
  }

  openDialog = (
    message: Message,
    type?: string,
    callback?: (result: any) => void,
    failcallback?: (result: any) => void
  ) => {
    if (message.message || type === 'gcode') {
      let dialogRef: any = () => {
        switch (type) {
          case 'alert':
            return this.dialog.open(AlertDialog);
          case 'error':
            return this.dialog.open(ErrorDialog);
          case 'confirm':
            return this.dialog.open(ConfirmDialog);
          case 'gcode':
            return this.dialog.open(GCodeDialog, {
              data: message.data ? message.data : undefined
            });
          case 'pdfinfo':
            return this.dialog.open(PdfInfoDialog);
          case 'workOffline':
            return this.dialog.open(WorkOfflineDialog);
          default:
            return this.dialog.open(AlertDialog);
        }
      };

      let instance = dialogRef().componentInstance;
      instance.message = message.message;
      instance.title = message.title;

      instance.data = message.data ? message.data : undefined;

      instance.user = message.user ? message.user : '';
      instance.company = message.company ? message.company : '';

      if (callback) {
        instance.dialogRef.afterClosed().subscribe(result => {
          if (failcallback) {
            if (result) {
              callback(result);
            } else {
              failcallback(result);
            }
          } else {
            if (result) {
              callback(result);
            }
          }
        });
      }
    }
  };

  extractedUsername = (value: string): string => {
    return value.substring(0, value.indexOf('@'));
  };

  inputChecker = (value: string, rulesToCheck: string[]): boolean => {
    /*
      we can set up a bunch of rules
    */
    let rules: object[] = [
      {
        name: 'required',
        regex: /.*\S.*/
      }
    ];

    for (let ruleToCheck of rulesToCheck) {
      let rule = _.find(rules, rule => ruleToCheck.toLowerCase() === rule['name'].toLowerCase());
      if (!rule.regex.test(value)) {
        return false;
      }
    }

    return true;
  };

  pascaleCase = (name: string): string => {
    if (name) {
      let arr, i, l;
      arr = String(name).split(/\s|_/);

      i = 0;
      l = arr.length;

      while (i < l) {
        arr[i] = arr[i].substr(0, 1).toUpperCase() + (arr[i].length > 1 ? arr[i].substr(1).toLowerCase() : '');
        i++;
      }

      return arr.join(' ');
    } else {
      return name;
    }
  };

  networkCheck = (callback?: (result: any) => void) => {
    // navigator is not reliable, because it only detects up the browser level
    // doing it this way, we are checking if there is any connection at all

    return this.cache.isOnline;
  };

  convertDecimalToInteger = (value: number): number => {
    let str = value.toString();
    let valueArray = str.split('.')[1];
    valueArray.length;
    // length is the place of the decimal
    //if 4 then 1000
    // 10 x 10 x 10 x 10

    let place = 1;

    for (let i = 1; i <= valueArray.length; i++) {
      place = 10 * place;
    }

    return value * place;
  };

  isApp = (): boolean => {
    // if its mobileweb, then we are done
    if (this.platform.is('mobileweb') || this.platform.is('mobile')) {
      return window.innerWidth < 851 ? true : false;
    } else {
      return window.innerWidth < 851 ? true : false;
    }
  };

  clearObject = (object: object, replaceWith: any): object => {
    Object.keys(object).map(k => {
      object[k] = replaceWith;
      if (typeof object[k] === 'object') {
        this.clearObject(object[k], replaceWith);
      }
    });

    return object;
  };

  filter = (items: object[], moreFilter?: object): object[] => {
    let retItems: object[] = items;

    if (moreFilter) {
      Object.keys(moreFilter).map(k => {
        retItems = retItems.filter(item => {
          if (item[k] && moreFilter[k]) {
            if (typeof item[k] === 'string') {
              return item[k].toLowerCase().indexOf(moreFilter[k].toLowerCase()) === 0;
            } else {
              return item[k] === moreFilter[k];
            }
          } else {
            return true;
          }
        });
      });
    }

    return retItems;
  };

  calculateAspectRatioFit = (srcWidth: number, srcHeight: number, maxWidth: number, maxHeight: number): object => {
    let ratio = math.min(maxWidth / srcWidth, maxHeight / srcHeight);

    return {
      width: srcWidth * ratio,
      height: srcHeight * ratio
    };
  };

  percentageOf = (input: number, min: number, max: number): number => {
    return ((input - min) * 100) / (max - min);
  };

  percentage = (input: number, min: number, max: number): number => {
    let k = max / input;
    return 100 / k;
  };

  /**
   * detect IE
   * returns version of IE or false, if browser is not Internet Explorer
   * for IE detection to add in special cases of CSS or code
   */
  detectIE = (): boolean => {
    let ua = window.navigator.userAgent;

    let ie = false;

    let msie = ua.indexOf('MSIE ');
    if (msie > 0) {
      // IE 10 or older => return version number
      ie = true;
    }

    let trident = ua.indexOf('Trident/');
    if (trident > 0) {
      // IE 11 => return version number
      ie = true;
    }

    let edge = ua.indexOf('Edge/');
    if (edge > 0) {
      // Edge (IE 12+) => return version number
      ie = true;
    }

    // other browser
    return ie;
  };

  centerPdfText = (doc, text, yCoordinate) => {
    let textWidth = (doc.getStringUnitWidth(text) * doc.internal.getFontSize()) / doc.internal.scaleFactor;
    var textOffset = (doc.internal.pageSize.width - textWidth) / 2;
    doc.text(textOffset, yCoordinate, text);
  };

  // Adds an array of items to the pdf. Set maxLineWidth to 0 to not jsPDF split text line
  listPdfText = (doc, items, xCord, startYCord, maxLineWidth, labelType, parameterHyperlink?) => {
    let yCord = startYCord;
    if (labelType === 'titles') {
      doc.setTextColor(100);
    } else {
      doc.setTextColor(0);
    }
    _.forEach(items, (item, index) => {
      if (!item) {
        item = '';
      }
      if (maxLineWidth > 0) {
        doc.text(doc.splitTextToSize(item.toString(), maxLineWidth), xCord, yCord);
        if (parameterHyperlink && !!parameterHyperlink[index]) {
          // Need to subtract the 3 from the ycord to line it up with the text from the added size
          doc.link(maxLineWidth + 2, yCord - 3, 3, 3, { url: parameterHyperlink[index] });
          doc.addImage(this.paramIcon, 'png', maxLineWidth + 2, yCord - 3, 3, 3);
        }
      } else {
        doc.text(item.toString(), xCord, yCord);
      }
      yCord += 7;
    });
  };

  difference = (object, base): object => {
    let changes = (object, base): object => {
      return _.transform(object, function(result, value, key) {
        if (!_.isEqual(value, base[key])) {
          result[key] = _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
        }
      });
    };
    return changes(object, base);
  };

  //Added try catch for apprentive so the app doesn't break.
  //Some cases Apptentive had an issue on their end or it tries to fire it when it's not supposed to be used
  apptentiveEngage = (
    event: string,
    customData?: object,
    successCallback?: (result: any) => void,
    failureCallback?: (result: any) => void
  ) => {
    let mobileWeb = this.platform.is('mobileweb') || !document.URL.startsWith('file');
    if (!mobileWeb && (this.platform.is('android') || this.platform.is('ios'))) {
      try {
        Apptentive.engage(successCallback, failureCallback, event, customData);
      } catch (error) {
        console.log('Error: ' + error);
      }
    }
  };

  openApptentiveMessageCenter = (successCallback?: (result: any) => void, failureCallback?: (result: any) => void) => {
    let mobileWeb = this.platform.is('mobileweb') || !document.URL.startsWith('file');
    if (!mobileWeb && (this.platform.is('android') || this.platform.is('ios'))) {
      try {
        Apptentive.showMessageCenter(successCallback, failureCallback, { app: 'Harvey Tool Milling Advisor' });
      } catch (error) {
        console.log('Error: ' + error);
      }
    }
  };
}
